import type { Media } from '../game/types';
import { Icon } from './Icon';
import { Stamp } from './Basics';

/**
 * Media slot of the question template. Kinds: phrase plate, image (with a designed missing state),
 * silhouette scan. The slot keeps its height when an image fails.
 */
export function MediaArea({ media, size = 'phone', revealed, revealName, scanNo = 1, height }:
  { media?: Media | null; size?: 'phone' | 'tv'; revealed?: boolean; revealName?: string; scanNo?: number; height?: number }) {
  if (!media) return null;
  const tv = size === 'tv';
  if (media.kind === 'phrase') {
    return (
      <figure className="ex-phrase mo-rise" style={{ margin: 0, ['--d' as string]: '200ms', ...(tv ? { padding: 36, gap: 14, alignSelf: 'start' } : null) }}>
        <span className="ex-mono" style={{ fontSize: tv ? 20 : 12, color: '#4A4F66' }}>{media.lang}</span>
        <span style={{ font: `italic 700 ${tv ? 52 : 22}px/1.15 var(--font-display)` }}>{media.original}</span>
        <span className="ex-mono" style={{ fontSize: tv ? 20 : 12, color: '#4A4F66', marginTop: tv ? 12 : 4 }}>Literally</span>
        <span style={{ fontSize: tv ? 36 : 17, lineHeight: 1.33 }}>{media.literal}</span>
      </figure>
    );
  }
  if (media.kind === 'image') {
    if (media.src) {
      return <figure style={{ margin: 0 }}><img src={media.src} alt={media.alt} style={{ display: 'block', width: '100%', height: height ?? (tv ? '100%' : 180), objectFit: 'cover', borderRadius: 12 }} /></figure>;
    }
    return (
      <figure className="ex-missing" style={{ margin: 0, height: height ?? (tv ? undefined : 120), gap: tv ? 16 : 8 }}>
        <Icon name="image" size={tv ? 64 : 28} />
        <figcaption style={{ fontSize: tv ? 28 : 15 }}>{tv ? 'Picture unavailable' : 'Picture unavailable. The question works without it.'}</figcaption>
      </figure>
    );
  }
  return <SilhouetteScan d={media.d} w={media.w} h={media.h} size={size} revealed={revealed} revealName={revealName} scanNo={scanNo} height={height} />;
}

/** Original "customs X-ray" treatment: pale outline, one scan pass, develops to gold on reveal. */
export function SilhouetteScan({ d, w, h, size = 'phone', revealed, revealName, scanNo = 1, height, showSource = true }:
  { d: string; w: number; h: number; size?: 'phone' | 'tv'; revealed?: boolean; revealName?: string; scanNo?: number; height?: number | string; showSource?: boolean }) {
  const tv = size === 'tv';
  return (
    <figure className="ex-scan" style={{ margin: 0, height: height ?? (tv ? '100%' : 200) }} aria-label={revealed && revealName ? `Outline of ${revealName}` : 'Outline of a country'}>
      <span className="ex-scan__tag" style={{ left: tv ? 24 : 12, top: tv ? 20 : 10, fontSize: tv ? 20 : 12 }}>Scan 0{scanNo} · {revealed ? 'identified' : 'applicant unknown'}</span>
      {tv && showSource && <span className="ex-scan__tag" style={{ right: 24, bottom: 20, fontSize: 16 }}>Outline: Natural Earth</span>}
      <svg viewBox={`-30 -30 ${w + 60} ${h + 60}`} preserveAspectRatio="xMidYMid meet" aria-hidden="true"
        style={{ position: 'absolute', left: tv ? 40 : 14, top: tv ? 60 : 30, width: `calc(100% - ${tv ? 80 : 28}px)`, height: `calc(100% - ${tv ? 110 : 44}px)` }}>
        <path className={revealed ? 'ex-sil ex-sil--reveal' : 'ex-sil'} d={d} style={revealed ? { animationDelay: '1900ms' } : undefined} />
      </svg>
      {!revealed && <span className="ex-scan__line" aria-hidden="true" />}
      {revealed && revealName && !tv && (
        <Stamp variant="gold" delay={2900} size={18} style={{ position: 'absolute', right: 12, bottom: 12, background: 'rgba(5,13,42,.7)' }}>{revealName}</Stamp>
      )}
    </figure>
  );
}
